export type AnalysisInfoItem = { Key: string; Value: string };

export const FILE_READING_FIELD_NAMES = ["method", "technique", "masterslave"] as const;

export type FileReadingFieldName = (typeof FILE_READING_FIELD_NAMES)[number];

export type FileReadingPreset = {
    method: "bflash" | "autotuner";
    masterslave: "slave" | "master";
};

/** Map uploaded file extension to step-1 reading method defaults. */
export const getFileReadingPreset = (name: string): FileReadingPreset | null => {
    const lower = name.toLowerCase();
    if (lower.endsWith(".sbdc")) {
        return { method: "bflash", masterslave: "slave" };
    }
    if (lower.endsWith(".bdc")) {
        return { method: "bflash", masterslave: "master" };
    }
    if (lower.endsWith(".slave")) {
        return { method: "autotuner", masterslave: "slave" };
    }
    return null;
};

/** Remove step-1 reading fields so vehicle autofill cannot overwrite file presets. */
export const stripReadingFieldsFromUpdates = <T extends Record<string, unknown>>(
    updates: T
): AnalysisFormUpdates => {
    const result: AnalysisFormUpdates = {};
    for (const [key, value] of Object.entries(updates)) {
        if (
            FILE_READING_FIELD_NAMES.includes(key as FileReadingFieldName) ||
            typeof value !== "string"
        ) {
            continue;
        }
        (result as Record<string, string>)[key] = value;
    }
    return result;
};

export type AnalysisFormUpdates = Partial<{
    calculator: string;
    prepared: string;
    sw: string;
    hw: string;
    vin: string;
    mileage: string;
    power: string;
    make: string;
    model: string;
    engine: string;
    year: string;
}>;

const isKnownInfoValue = (value: string | undefined) =>
    !!value?.trim() && value.trim().toUpperCase() !== "UNKNOWN";

/** Uppercase first letter of every word; split on whitespace, `-`, and `/`. */
export const toTitleCaseWords = (value: string): string =>
    value
        .split(/([\s\-/]+)/)
        .map((part) =>
            /^[\s\-/]+$/.test(part) || !part
                ? part
                : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
        )
        .join("");

const getInfoValue = (infos: AnalysisInfoItem[] | undefined, key: string) =>
    infos?.find((i) => i.Key?.toUpperCase() === key.toUpperCase())?.Value?.trim();

const extractInfos = (data: unknown): AnalysisInfoItem[] | undefined => {
    if (!data || typeof data !== "object") return undefined;
    const roots = [data, (data as { data?: unknown }).data].filter(
        (r): r is Record<string, unknown> => !!r && typeof r === "object"
    );
    for (const root of roots) {
        const infos = root.Infos ?? root.infos;
        if (Array.isArray(infos)) return infos as AnalysisInfoItem[];
    }
    return undefined;
};

const findPrimaryProjectFile = (data: Record<string, unknown>): Record<string, unknown> | null => {
    const best = data.bestMatchingFile as { file?: Record<string, unknown> } | undefined;
    if (best?.file) return best.file;

    const projects = data.projects;
    if (!Array.isArray(projects)) return null;

    for (const project of projects) {
        const files = project?.files;
        if (!Array.isArray(files)) continue;
        const original = files.find(
            (f: { type?: string }) => (f?.type || "").toLowerCase() === "original"
        );
        if (original) return original;
        if (files[0]) return files[0];
    }
    return null;
};

const assignIfKnown = (
    updates: AnalysisFormUpdates,
    key: keyof AnalysisFormUpdates,
    value: string | undefined,
    transform: (v: string) => string = (v) => v
) => {
    if (isKnownInfoValue(value)) {
        updates[key] = transform(value!);
    }
};

const getUpdatesFromInfos = (infos: AnalysisInfoItem[] | undefined): AnalysisFormUpdates => {
    const updates: AnalysisFormUpdates = {};
    assignIfKnown(updates, "calculator", getInfoValue(infos, "SUPPLIER"), toTitleCaseWords);
    assignIfKnown(updates, "prepared", getInfoValue(infos, "CONTROL-UNIT"));
    //assignIfKnown(updates, "engine", getInfoValue(infos, "FAMILY"), toTitleCaseWords);
    assignIfKnown(updates, "sw", getInfoValue(infos, "SOFTWARE"));
    assignIfKnown(updates, "hw", getInfoValue(infos, "HARDWARE"));
    assignIfKnown(updates, "vin", getInfoValue(infos, "VIN"), (v) => v.toUpperCase());
    assignIfKnown(updates, "mileage", getInfoValue(infos, "VEHICLE-MILEAGE"));
    assignIfKnown(updates, "power", getInfoValue(infos, "VEHICLE-POWER"));
    assignIfKnown(updates, "make", getInfoValue(infos, "VEHICLE-MAKE"), toTitleCaseWords);
    assignIfKnown(updates, "model", getInfoValue(infos, "VEHICLE-MODEL"), toTitleCaseWords);
    return updates;
};

const getUpdatesFromProjectFile = (file: Record<string, unknown>): AnalysisFormUpdates => {
    const updates: AnalysisFormUpdates = {};
    const str = (k: string) => {
        const v = file[k];
        return typeof v === "string" ? v.trim() : v != null ? String(v).trim() : undefined;
    };

    assignIfKnown(updates, "make", str("vehicleProducer"), toTitleCaseWords);
    assignIfKnown(updates, "model", str("vehicleModel"), toTitleCaseWords);
    assignIfKnown(updates, "engine", str("engineType"), toTitleCaseWords);
    assignIfKnown(updates, "year", str("vehicleModelyear"));
    assignIfKnown(updates, "sw", str("ecuSoftwareVersion"));
    assignIfKnown(updates, "hw", str("ecuProdNr"));
    assignIfKnown(updates, "power", str("enginePower") || str("userdef1"));
    assignIfKnown(updates, "vin", str("userdef2"), (v) => v.toUpperCase());
    return updates;
};

/** Build form field updates from file-analyzer / decrypt-and-analyze payloads. */
export const getFormUpdatesFromAnalysis = (data: unknown): AnalysisFormUpdates => {
    if (!data || typeof data !== "object") return {};

    const record = data as Record<string, unknown>;
    const fromInfos = getUpdatesFromInfos(extractInfos(data));
    const projectFile = findPrimaryProjectFile(record);
    const fromProject = projectFile ? getUpdatesFromProjectFile(projectFile) : {};

    return { ...fromProject, ...fromInfos };
};
